import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Save, Trash2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const DEFAULT_COLORS = [
  "#059669",
  "#f59e0b",
  "#8b5cf6",
  "#0ea5e9",
  "#6b7280",
  "#dc2626",
  "#ec4899",
  "#14b8a6",
];

function StatusRow({ status, onSave, onDelete }) {
  const [draft, setDraft] = useState(status);
  const [busy, setBusy] = useState(false);
  const dirty =
    draft.name !== status.name ||
    draft.color !== status.color ||
    draft.order !== status.order ||
    draft.active !== status.active;

  const doSave = async () => {
    setBusy(true);
    try {
      await onSave(draft);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-white p-3"
      data-testid={`platform-status-row-${status.id}`}
    >
      <input
        type="color"
        value={draft.color || "#6b7280"}
        onChange={(e) => setDraft({ ...draft, color: e.target.value })}
        className="h-9 w-12 cursor-pointer rounded border border-zinc-200"
      />
      <Input
        value={draft.name}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        className="max-w-xs flex-1"
      />
      <Input
        type="number"
        value={draft.order ?? 0}
        onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value || "0", 10) })}
        className="w-20"
      />
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        <Switch
          checked={draft.active !== false}
          onCheckedChange={(v) => setDraft({ ...draft, active: v })}
        />
        <span>{draft.active !== false ? "Aktif" : "Kapalı"}</span>
      </div>
      <div className="ml-auto flex items-center gap-1">
        <Button
          size="sm"
          onClick={doSave}
          disabled={!dirty || busy}
          className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Kaydet
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(status)}
          className="text-red-600 hover:bg-red-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function StatusList({ kind }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(DEFAULT_COLORS[0]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/platform/contact-statuses?kind=${kind}`);
      setRows(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/platform/contact-statuses", {
        kind,
        name,
        color: newColor,
        order: rows.length,
      });
      setRows((cur) => [...cur, data]);
      setCreating(false);
      setNewName("");
      toast.success(t("common.saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const save = async (draft) => {
    try {
      const { data } = await api.patch(`/platform/contact-statuses/${draft.id}`, {
        name: draft.name,
        color: draft.color,
        order: draft.order,
        active: draft.active,
      });
      setRows((cur) => cur.map((r) => (r.id === data.id ? data : r)));
      toast.success(t("common.saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Silinsin mi: "${row.name}"?`)) return;
    try {
      await api.delete(`/platform/contact-statuses/${row.id}`);
      setRows((cur) => cur.filter((r) => r.id !== row.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">
          {loading ? "…" : `${rows.length} statü`}
        </div>
        {!creating && (
          <Button
            size="sm"
            onClick={() => setCreating(true)}
            data-testid={`platform-add-status-${kind}`}
            className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("platform_contacts.add")}
          </Button>
        )}
      </div>

      {creating && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-3">
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="h-9 w-12 cursor-pointer rounded border border-zinc-200"
          />
          <Input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("platform_contacts.field_name")}
            onKeyDown={(e) => e.key === "Enter" && create()}
            className="max-w-xs flex-1"
          />
          <Button size="sm" onClick={create} className="bg-zinc-900 text-white hover:bg-zinc-800">
            Ekle
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>
            İptal
          </Button>
        </div>
      )}

      {rows.length === 0 && !loading ? (
        <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-8 text-center text-sm text-zinc-500">
          {t("platform_contacts.empty")}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <StatusRow key={r.id} status={r} onSave={save} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlatformContactStatuses() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6" data-testid="platform-contact-statuses-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-zinc-950 sm:text-3xl">
          {t("platform_contacts.title")}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          {t("platform_contacts.subtitle")}
        </p>
      </div>

      <Tabs defaultValue="person">
        <TabsList className="bg-zinc-100">
          <TabsTrigger value="person" data-testid="tab-status-person">
            {t("platform_contacts.tab_people")}
          </TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-status-company">
            {t("platform_contacts.tab_companies")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="person" className="mt-4">
          <StatusList kind="person" />
        </TabsContent>
        <TabsContent value="company" className="mt-4">
          <StatusList kind="company" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
